import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Anchor, LayoutGrid } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface Marina {
  id: number;
  name: string;
}

interface Props {
  marinas: Marina[];
}

export function MarinaSwitcher({ marinas }: Props) {
  const { activeMarinaId, setActiveMarina } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeMarina = marinas.find(m => m.id === activeMarinaId);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleSelect = (marina: Marina) => {
    setActiveMarina(marina.id);
    setOpen(false);
    // Stay on current page — data will reload via activeMarinaId change
  };

  const handleAllMarinas = () => {
    setActiveMarina(null);
    setOpen(false);
    navigate('/org');
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border border-gray-200 bg-white hover:bg-gray-50 transition-colors max-w-[200px]"
      >
        <Anchor className="w-3.5 h-3.5 text-brand-500 shrink-0" />
        <span className="truncate font-medium text-gray-800">
          {activeMarina?.name ?? 'Select marina'}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-56 max-w-[min(14rem,calc(100vw-1rem))] bg-white rounded-xl border border-gray-200 shadow-lg z-50 py-1 overflow-hidden">
          <button
            onClick={handleAllMarinas}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-brand-600 hover:bg-brand-50 transition-colors"
          >
            <LayoutGrid className="w-4 h-4" />
            ← All Marinas
          </button>
          <div className="h-px bg-gray-100 my-1" />
          {marinas.map(marina => (
            <button
              key={marina.id}
              onClick={() => handleSelect(marina)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                marina.id === activeMarinaId ? 'text-brand-700 font-medium bg-brand-50/50' : 'text-gray-700'
              }`}
            >
              <Anchor className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{marina.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
